import React, { useState, useRef, useEffect } from 'react';
import { SignatureCanvas } from '@/components/SignatureCanvas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function VisitorRegistration() {
  const [formData, setFormData] = useState({
    visitor_name: '',
    visitor_company: '',
    visitor_contact: '',
    subject: '',
    companions: '',
    has_vehicle: false,
    vehicle_plates: '',
    department_id: '',
    employee_id: '',
  });
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const signatureRef = useRef(null);

  useEffect(() => {
    api.getDepartments()
      .then(res => setDepartments(res.data))
      .catch(err => console.error('Error loading departments:', err));
  }, []);

  useEffect(() => {
    if (formData.department_id) {
      api.getEmployeesByDepartment(formData.department_id)
        .then(res => setEmployees(res.data))
        .catch(err => console.error('Error loading employees:', err));
    } else {
      setEmployees([]);
      setFormData(prev => ({ ...prev, employee_id: '' }));
    }
  }, [formData.department_id]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (signatureRef.current?.isEmpty()) {
      toast.error('Por favor, agregue su firma');
      return;
    }

    if (!formData.visitor_name || formData.visitor_name.length < 10) {
      toast.error('El nombre debe tener al menos 10 caracteres');
      return;
    }

    if (!formData.visitor_company || formData.visitor_company.length < 2) {
      toast.error('El nombre de la empresa debe tener al menos 2 caracteres');
      return;
    }

    if (!formData.visitor_contact || formData.visitor_contact.length < 10) {
      toast.error('El teléfono o correo debe tener al menos 10 caracteres');
      return;
    }

    if (!formData.subject || formData.subject.length < 10) {
      toast.error('El asunto debe tener al menos 10 caracteres');
      return;
    }

    if (!formData.department_id || !formData.employee_id) {
      toast.error('Seleccione departamento y empleado');
      return;
    }

    setLoading(true);
    try {
      const signatureData = signatureRef.current.toDataURL();
      await api.createVisit({ ...formData, signature_data: signatureData });
      setSubmitted(true);
      toast.success('Registro completado exitosamente');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al registrar la visita');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      visitor_name: '',
      visitor_company: '',
      visitor_contact: '',
      subject: '',
      companions: '',
      has_vehicle: false,
      vehicle_plates: '',
      department_id: '',
      employee_id: '',
    });
    setSubmitted(false);
    signatureRef.current?.clear();
  };

  if (submitted) {
    return (
      <div className="min-h-screen relative flex items-center justify-center p-4"
        style={{
          backgroundImage: 'url(/assets/bg.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative max-w-md w-full bg-white rounded-lg shadow-2xl p-8 text-center" data-testid="success-message">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-900 mb-2 uppercase tracking-tight">¡Registro Exitoso!</h2>
          <p className="text-gray-600 mb-6">Su entrada ha sido registrada. Por favor espere la aprobación del vigilante.</p>
          <Button
            onClick={handleReset}
            data-testid="new-registration-button"
            className="bg-[#DC143C] hover:bg-[#B91C1C] text-white font-medium uppercase tracking-wide px-8 py-3"
          >
            Nuevo Registro
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative"
      style={{
        backgroundImage: 'url(/assets/bg.webp)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="absolute inset-0 bg-black/40" />
      
      <div className="relative min-h-screen flex flex-col">
        {/* Header */}
        <div className="bg-white/95 backdrop-blur-sm shadow-md">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src="/assets/logo.jpg" alt="Proesa" className="h-12 md:h-16" />
              <div>
                <p className="text-lg md:text-2xl text-gray-700 uppercase tracking-wide font-semibold">Registro de Visitantes</p>
              </div>
            </div>
            <Link to="/login">
              <Button variant="outline" size="sm" className="text-sm uppercase tracking-wide">
                Acceso Personal
              </Button>
            </Link>
          </div>
        </div>

        {/* Form */}
        <div className="flex-1 flex items-center justify-center p-4 md:p-8">
          <div className="w-full max-w-2xl bg-white/95 backdrop-blur-md rounded-lg shadow-2xl p-6 md:p-10" data-testid="visitor-registration-form">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 uppercase tracking-tight text-center">Registro de Entrada</h2>
            <p className="text-center text-gray-600 mb-8 text-sm">Complete el formulario para registrar su visita</p>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="visitor_name" className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Nombre del Visitante *</Label>
                <Input
                  id="visitor_name"
                  data-testid="visitor-name-input"
                  value={formData.visitor_name}
                  onChange={(e) => setFormData({ ...formData, visitor_name: e.target.value })}
                  placeholder="Nombre completo (mínimo 10 caracteres)"
                  className="h-11 border-gray-300 focus:border-[#DC143C] focus:ring-[#DC143C]"
                  required
                />
              </div>

              <div>
                <Label htmlFor="visitor_company" className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Empresa del Visitante *</Label>
                <Input
                  id="visitor_company"
                  data-testid="visitor-company-input"
                  value={formData.visitor_company}
                  onChange={(e) => setFormData({ ...formData, visitor_company: e.target.value })}
                  placeholder="Nombre de la empresa (mínimo 2 caracteres)"
                  className="h-11 border-gray-300 focus:border-[#DC143C] focus:ring-[#DC143C]"
                  required
                />
              </div>

              <div>
                <Label htmlFor="subject" className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Asunto / Motivo de la Visita *</Label>
                <Input
                  id="subject"
                  data-testid="subject-input"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Descripción del motivo (mínimo 10 caracteres)"
                  className="h-11 border-gray-300 focus:border-[#DC143C] focus:ring-[#DC143C]"
                  required
                />
              </div>

              <div>
                <Label htmlFor="visitor_contact" className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Teléfono o Correo Electrónico *</Label>
                <Input
                  id="visitor_contact"
                  data-testid="visitor-contact-input"
                  value={formData.visitor_contact}
                  onChange={(e) => setFormData({ ...formData, visitor_contact: e.target.value })}
                  placeholder="Teléfono o email de contacto"
                  className="h-11 border-gray-300 focus:border-[#DC143C] focus:ring-[#DC143C]"
                  required
                />
              </div>

              <div>
                <Label htmlFor="companions" className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Acompañantes (Opcional)</Label>
                <Input
                  id="companions"
                  data-testid="companions-input"
                  value={formData.companions}
                  onChange={(e) => setFormData({ ...formData, companions: e.target.value })}
                  placeholder="Nombres de acompañantes"
                  className="h-11 border-gray-300 focus:border-[#DC143C] focus:ring-[#DC143C]"
                />
              </div>

              <div className="flex items-center space-x-3">
                <Checkbox
                  id="has_vehicle"
                  data-testid="vehicle-checkbox"
                  checked={formData.has_vehicle}
                  onCheckedChange={(checked) => setFormData({ ...formData, has_vehicle: checked })}
                  className="border-gray-300"
                />
                <Label htmlFor="has_vehicle" className="text-sm font-medium text-gray-700 uppercase tracking-wide cursor-pointer">
                  ¿Ingresa con Vehículo?
                </Label>
              </div>

              {formData.has_vehicle && (
                <div>
                  <Label htmlFor="vehicle_plates" className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Placas del Vehículo</Label>
                  <Input
                    id="vehicle_plates"
                    data-testid="vehicle-plates-input"
                    value={formData.vehicle_plates}
                    onChange={(e) => setFormData({ ...formData, vehicle_plates: e.target.value })}
                    placeholder="Ingrese las placas del vehículo"
                    className="h-11 border-gray-300 focus:border-[#DC143C] focus:ring-[#DC143C]"
                  />
                </div>
              )}

              <div>
                <Label htmlFor="department" className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Departamento *</Label>
                <Select value={formData.department_id} onValueChange={(value) => setFormData({ ...formData, department_id: value })}>
                  <SelectTrigger id="department" data-testid="department-select" className="h-11 border-gray-300 focus:border-[#DC143C] focus:ring-[#DC143C]">
                    <SelectValue placeholder="Seleccione un departamento" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map(dept => (
                      <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="employee" className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Empleado a Visitar *</Label>
                <Select value={formData.employee_id} onValueChange={(value) => setFormData({ ...formData, employee_id: value })} disabled={!formData.department_id}>
                  <SelectTrigger id="employee" data-testid="employee-select" className="h-11 border-gray-300 focus:border-[#DC143C] focus:ring-[#DC143C]">
                    <SelectValue placeholder={formData.department_id ? "Seleccione un empleado" : "Primero seleccione un departamento"} />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium text-gray-700 mb-1.5 block uppercase tracking-wide">Firma del Visitante *</Label>
                <SignatureCanvas ref={signatureRef} />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  data-testid="submit-registration-button"
                  disabled={loading}
                  className="flex-1 bg-[#DC143C] hover:bg-[#B91C1C] text-white font-medium uppercase tracking-wide py-3 active:scale-95 transition-transform"
                >
                  {loading ? 'Registrando...' : 'Registrar Entrada'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReset}
                  data-testid="cancel-button"
                  className="border-gray-300 text-gray-700 hover:bg-gray-50 font-medium uppercase tracking-wide"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
